import React from 'react';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { industriesData } from '../mockData';

export const Solutions = () => {
  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            AI Solutions by Industry
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Industry-specific AI transformation solutions delivering measurable ROI
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              AI Transformation Across Industries
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Proven AI solutions tailored to your industry's unique challenges
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {industriesData.map((industry) => {
              const IconComponent = LucideIcons[industry.icon];
              return (
                <Card key={industry.id} className="border-2 border-gray-200 hover:border-blue-900 hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-6">
                      <div className="w-16 h-16 bg-blue-900 rounded-xl flex items-center justify-center flex-shrink-0">
                        <IconComponent className="text-white" size={28} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">
                          {industry.name}
                        </h3>
                        <p className="text-gray-600 mb-4">
                          {industry.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Problem-Solution-Results */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              AI Transformation in Action
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real business challenges solved with enterprise AI solutions
            </p>
          </div>

          <div className="space-y-12">
            {industriesData.map((industry, index) => {
              const IconComponent = LucideIcons[industry.icon];
              return (
                <Card key={industry.id} className="border-0 shadow-lg overflow-hidden">
                  <CardContent className="p-0">
                    <div className="grid lg:grid-cols-3">
                      {/* Problem */}
                      <div className="p-8 bg-red-50 border-l-4 border-red-500">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">Problem</h3>
                        </div>
                        <p className="text-gray-700">{industry.problem}</p>
                      </div>

                      {/* Solution */}
                      <div className="p-8 bg-blue-50 border-l-4 border-blue-900">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-blue-900 rounded-lg flex items-center justify-center">
                            <IconComponent className="text-white" size={20} />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">Solution</h3>
                        </div>
                        <p className="text-gray-700">{industry.solution}</p>
                      </div>

                      {/* Results */}
                      <div className="p-8 bg-green-50 border-l-4 border-green-600">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">Results</h3>
                        </div>
                        <p className="text-gray-700">{industry.results}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our AI Transformation Approach
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive methodology for successful AI implementation
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'AI Strategy & Planning',
                items: ['AI maturity assessment', 'Use case identification', 'ROI modeling', 'Technology selection', 'Change management planning']
              },
              {
                title: 'AI Development & Deployment',
                items: ['Custom AI model development', 'GenAI application building', 'Model fine-tuning', 'System integration', 'Production deployment']
              },
              {
                title: 'Optimization & Scale',
                items: ['Model monitoring & retraining', 'Performance optimization', 'Cost optimization', 'Scaling infrastructure', 'Continuous improvement']
              }
            ].map((capability, index) => (
              <Card key={index} className="border-2 border-gray-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {capability.title}
                  </h3>
                  <ul className="space-y-3">
                    {capability.items.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-blue-900 rounded-full" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Business with AI?
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Let's discuss how AI can solve your specific industry challenges
          </p>
          <Button 
            size="lg"
            className="bg-white text-blue-900 hover:bg-gray-100 text-lg px-10 py-6 h-auto font-semibold"
            onClick={() => window.location.href = '/contact'}
          >
            Get in Touch
            <LucideIcons.ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>
    </div>
  );
};
